// R1b-β Step 3 Deliverable G — Approval detail page.
// Server-authoritative eligibility; buttons render disabled with tooltip
// showing the exact SoD/role reason when the caller isn't eligible.
import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { AdminAppShell } from "../../design-system/shells/AdminAppShell";
import { useAuth } from "../../lib/auth";
import { api, formatApiError } from "../../lib/api";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import StatusChip from "../../components/StatusChip";
import { toast } from "sonner";
import { ShieldCheck, CheckSquare, XSquare, Play, ClipboardList, AlertTriangle } from "lucide-react";
import InformationIntake, { AttachmentsPanel } from "../../design-system/intake/InformationIntake";
import ComplianceProofButton from "../../design-system/compliance/ComplianceProofButton";
// Release C-2 (P1) — Open in Microsoft 365 Mail for the approval detail surface.
import OpenInMicrosoftMailButton from "../../design-system/microsoft365/OpenInMicrosoftMailButton";

export default function ApprovalDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [req, setReq] = useState(null);
  const [reason, setReason] = useState("");
  const [idempKey, setIdempKey] = useState("");
  const [execNotes, setExecNotes] = useState("");
  const [verifyNotes, setVerifyNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [attachReload, setAttachReload] = useState(0);

  const load = async () => {
    try {
      const r = await api.get(`/approvals/${id}`);
      setReq(r.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!req) {
    return (
      <AdminAppShell activeNav="people-access" title="Approval" subtitle="Loading…" user={user}>
        <div className="mx-auto w-full max-w-[1600px] px-4 md:px-6 lg:px-8" data-testid="approval-detail-loading">
          <div className="font-mono text-xs text-erf-dark/60">Loading approval…</div>
        </div>
      </AdminAppShell>
    );
  }

  const isRequester = user?.id === req.requested_by;
  const isOwner = user?.role_key === "OWNER_SUPER_ADMIN";
  const status = req.overall_status;
  const currentStep = req.steps?.[req.current_step];
  const callerIsDesignated = currentStep && (
    (currentStep.approver_subject_type === "user" && currentStep.approver_subject_id === user?.id) ||
    (currentStep.approver_subject_type === "role" && currentStep.approver_subject_id === user?.role_key)
  );

  let decideDisabledReason = null;
  if (status !== "pending") decideDisabledReason = `State is ${status}, not pending.`;
  else if (isRequester && !req.sod_overridden) decideDisabledReason = "SoD: you are the requester.";
  else if (!callerIsDesignated && !isOwner) decideDisabledReason = "You are not the designated approver.";

  const rs = req.recommend_step;
  const canRecommend = status === "awaiting_recommendation" && rs && (rs.recommender_user_id === user?.id) && !isRequester;
  const recDisabledReason = status !== "awaiting_recommendation"
    ? `State is ${status}, not awaiting_recommendation.`
    : rs?.recommender_user_id !== user?.id ? "You are not the designated recommender."
    : isRequester ? "SoD: you cannot recommend your own request." : null;

  const canExecute = status === "approved";
  const canVerify = status === "executed";

  const call = async (path, body, action) => {
    setBusy(true);
    try {
      await api.post(path, body);
      toast.success(`${action} recorded`);
      await load();
      setReason(""); setIdempKey(""); setExecNotes(""); setVerifyNotes("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <AdminAppShell
      activeNav="people-access"
      title={`Approval · ${req.request_type}`}
      subtitle={`Requested ${(req.created_at || "").slice(0, 19)} UTC`}
      breadcrumbs={[
        { label: "People & Access", to: "/admin/people-roles/users" },
        { label: "Approvals", to: "/admin/approvals" },
        { label: req.id.slice(0, 8), to: null },
      ]}
      user={user}
    >
      <div className="mx-auto w-full max-w-[1600px] px-4 md:px-6 lg:px-8 grid gap-4 md:grid-cols-3" data-testid="approval-detail-page">
        <Card className="md:col-span-2 border-erf-borderCream bg-erf-cream" data-testid="approval-detail-meta">
          <CardContent className="p-5 space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-erf-gold">Request</div>
                <div className="font-display font-semibold text-erf-dark text-lg mt-1">{req.request_type}</div>
                <div className="text-xs font-mono text-erf-dark/70 mt-0.5">ID {req.id}</div>
              </div>
              <StatusChip status={status === "rejected" || status === "executed_verification_failed" ? "error" : status.includes("pending") || status === "awaiting_recommendation" ? "pending_approval" : "approved"} label={status.replace(/_/g, " ")} />
            </div>
            {/* FINAL-POLISH P2 — Open in Microsoft 365 Mail on the record header for Owner/Admin. */}
            <div className="mt-3">
              <OpenInMicrosoftMailButton surface="approval_detail" recordType="approval_request" recordId={req.id} />
            </div>
            <div className="grid grid-cols-2 gap-3 text-xs font-mono text-erf-dark/80">
              <div><span className="text-erf-dark/50">Requester:</span> {req.requested_by.slice(0, 8)}…</div>
              <div><span className="text-erf-dark/50">Payload ref:</span> {req.payload_ref || "—"}</div>
              <div><span className="text-erf-dark/50">Attrs:</span> {JSON.stringify(req.attrs || {})}</div>
              <div><span className="text-erf-dark/50">SoD overridden:</span> {req.sod_overridden ? "yes" : "no"}</div>
            </div>

            {rs && (
              <div className="border border-erf-borderCream rounded-md p-3 bg-white" data-testid="approval-recommend-step">
                <div className="text-[10px] font-mono uppercase tracking-widest text-erf-gold mb-1">Recommend step</div>
                <div className="text-xs font-mono text-erf-dark/80">
                  Recommender: {rs.recommender_user_id.slice(0, 8)}… · {rs.recommended === true ? "recommended" : rs.recommended === false ? "declined" : "awaiting"}
                </div>
                {rs.reason && <div className="text-xs mt-1 text-erf-dark/70">Reason: {rs.reason}</div>}
                {canRecommend ? (
                  <div className="mt-2 space-y-2">
                    <Textarea placeholder="Reason (≥ 3 chars)" value={reason} onChange={(e) => setReason(e.target.value)}
                              rows={2} data-testid="approval-recommend-reason" />
                    <div className="flex gap-2">
                      <Button size="sm" disabled={busy || reason.length < 3}
                              onClick={() => call(`/approvals/${id}/recommend`, { decision: "recommend", reason }, "Recommended")}
                              className="bg-erf-gold text-erf-dark" data-testid="approval-recommend-btn">
                        <CheckSquare className="w-4 h-4 mr-1" /> Recommend
                      </Button>
                      <Button size="sm" variant="outline" disabled={busy || reason.length < 3}
                              onClick={() => call(`/approvals/${id}/recommend`, { decision: "decline_recommendation", reason }, "Declined")}
                              className="border-erf-borderCream" data-testid="approval-recommend-decline-btn">
                        <XSquare className="w-4 h-4 mr-1" /> Decline
                      </Button>
                    </div>
                  </div>
                ) : recDisabledReason && (
                  <div className="text-[11px] mt-1 text-erf-dark/50 italic" title={recDisabledReason} data-testid="approval-recommend-disabled">{recDisabledReason}</div>
                )}
              </div>
            )}

            <div className="border border-erf-borderCream rounded-md p-3 bg-white" data-testid="approval-steps">
              <div className="text-[10px] font-mono uppercase tracking-widest text-erf-gold mb-1">Decision steps</div>
              {(req.steps || []).map((s, i) => (
                <div key={i} className="flex items-center justify-between text-xs font-mono border-t border-erf-borderCream/50 py-1.5 first:border-t-0">
                  <div>
                    Step {i + 1} · {s.approver_subject_type}:{s.approver_subject_id}
                    {i === req.current_step && status === "pending" && <span className="ml-2 text-erf-gold uppercase text-[9px]">current</span>}
                  </div>
                  <div className="text-erf-dark/70">{s.status}{s.reason ? ` · ${s.reason}` : ""}</div>
                </div>
              ))}
              {status === "pending" && (
                <div className="mt-2 space-y-2">
                  <Textarea placeholder="Decision reason (≥ 3 chars)" value={reason} onChange={(e) => setReason(e.target.value)}
                            rows={2} disabled={!!decideDisabledReason} data-testid="approval-decide-reason" />
                  <div className="flex gap-2">
                    <Button size="sm" disabled={busy || reason.length < 3 || !!decideDisabledReason}
                            onClick={() => call(`/approvals/${id}/decide`, { decision: "approve", reason }, "Approved")}
                            title={decideDisabledReason || ""} className="bg-erf-gold text-erf-dark"
                            data-testid="approval-decide-approve">
                      <CheckSquare className="w-4 h-4 mr-1" /> Approve
                    </Button>
                    <Button size="sm" variant="outline" disabled={busy || reason.length < 3 || !!decideDisabledReason}
                            onClick={() => call(`/approvals/${id}/decide`, { decision: "reject", reason }, "Rejected")}
                            title={decideDisabledReason || ""} className="border-erf-borderCream text-red-700"
                            data-testid="approval-decide-reject">
                      <XSquare className="w-4 h-4 mr-1" /> Reject
                    </Button>
                    {decideDisabledReason && (
                      <div className="text-[11px] mt-1 text-erf-dark/50 italic ml-2 self-center" data-testid="approval-decide-disabled">
                        <AlertTriangle className="w-3 h-3 inline mr-1"/>{decideDisabledReason}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {(canExecute || canVerify || (req.executed_operations || []).length > 0) && (
              <div className="border border-erf-borderCream rounded-md p-3 bg-white" data-testid="approval-execute-block">
                <div className="text-[10px] font-mono uppercase tracking-widest text-erf-gold mb-1">Execute &amp; Verify</div>
                {(req.executed_operations || []).map((op, i) => (
                  <div key={i} className="text-xs font-mono text-erf-dark/70">
                    Executed {op.executed_at?.slice(0, 19)} · key {op.idempotency_key.slice(0, 12)}…{op.notes ? ` · ${op.notes}` : ""}
                  </div>
                ))}
                {canExecute && (
                  <div className="mt-2 space-y-2">
                    <Input placeholder="Idempotency key (≥ 6 chars)" value={idempKey} onChange={(e) => setIdempKey(e.target.value)}
                           data-testid="approval-execute-idem" className="font-mono text-xs" />
                    <Textarea placeholder="Execution notes (optional)" value={execNotes} onChange={(e) => setExecNotes(e.target.value)}
                              rows={2} data-testid="approval-execute-notes" />
                    <Button size="sm" disabled={busy || idempKey.length < 6}
                            onClick={() => call(`/approvals/${id}/execute`, { idempotency_key: idempKey, execution_notes: execNotes || null }, "Executed")}
                            className="bg-erf-gold text-erf-dark" data-testid="approval-execute-btn">
                      <Play className="w-4 h-4 mr-1" /> Execute
                    </Button>
                  </div>
                )}
                {canVerify && (
                  <div className="mt-2 space-y-2">
                    <Textarea placeholder="Verification notes (optional)" value={verifyNotes} onChange={(e) => setVerifyNotes(e.target.value)}
                              rows={2} data-testid="approval-verify-notes" />
                    <div className="flex gap-2">
                      <Button size="sm" disabled={busy}
                              onClick={() => call(`/approvals/${id}/verify`, { verified: true, verification_notes: verifyNotes || null }, "Verified")}
                              className="bg-erf-gold text-erf-dark" data-testid="approval-verify-ok">
                        <ShieldCheck className="w-4 h-4 mr-1" /> Mark verified
                      </Button>
                      <Button size="sm" variant="outline" disabled={busy}
                              onClick={() => call(`/approvals/${id}/verify`, { verified: false, verification_notes: verifyNotes || null }, "Verification failed")}
                              className="border-erf-borderCream text-red-700" data-testid="approval-verify-fail">
                        <XSquare className="w-4 h-4 mr-1" /> Verification failed
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="md:col-span-2 border-erf-borderCream bg-erf-cream" data-testid="approval-evidence">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-erf-gold">Evidence &amp; documents</div>
                <div className="font-display font-semibold text-erf-dark">Attached to this approval</div>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <ComplianceProofButton recordType="approval_request" recordId={req.id} />
                <InformationIntake
                  recordType="approval_request" recordId={req.id}
                  category="other" confidentiality="confidential"
                  capabilities={["upload", "multi", "paste", "existing", "photo", "scan", "request_info"]}
                  onChanged={() => setAttachReload((k) => k + 1)}
                />
              </div>
            </div>
            <AttachmentsPanel recordType="approval_request" recordId={req.id} reload={attachReload} />
          </CardContent>
        </Card>

        <Card className="border-erf-borderCream bg-erf-cream" data-testid="approval-audit-trail">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-2">
              <ClipboardList className="w-4 h-4 text-erf-gold" />
              <div className="font-display font-semibold text-erf-dark">Audit trail</div>
            </div>
            <div className="space-y-1 max-h-[520px] overflow-y-auto">
              {(req.audit_trail || []).length === 0 && (
                <div className="text-xs font-mono text-erf-dark/50">No audit rows for this approval.</div>
              )}
              {(req.audit_trail || []).map((r) => (
                <div key={r.id} className="text-[11px] font-mono border-b border-erf-borderCream/40 py-1"
                     data-testid={`approval-audit-row-${r.id}`}>
                  <div className="text-erf-dark/60">{(r.ts || "").slice(0, 19)}</div>
                  <div className="text-erf-dark"><span className="text-erf-gold">{r.action}</span> · {r.actor_role}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminAppShell>
  );
}
